import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import { mailApi } from "../../api/mailApi";
import { notify } from "../../../../util/toast";

/**
 * 메일 받는이/참조/숨은참조 자동완성 — 직원 + 내 개인 연락처 통합.
 *
 * <h3>왜 분리했는가</h3>
 * 이전엔 MailWriteModal 내부에 인라인 함수({@code AddressAutocomplete})로 존재했다. 메일 도메인
 * 자동완성의 시각·동작 표준을 한 곳에 잠그고, 다른 메일 화면(추후 전달/회신 등 별도 form 이
 * 생겼을 때)에서도 같은 컴포넌트를 호출할 수 있도록 외부 컴포넌트로 격상.
 *
 * <h3>표준 동작 (= 메일 받는이 디자인의 원형)</h3>
 * <ul>
 *   <li><b>그룹화</b> — "직원" / "내 연락처" 두 그룹. 직원이 항상 위.</li>
 *   <li><b>중복 제거</b> — 동일 email 이 양쪽에 있을 때 직원 쪽이 이긴다.</li>
 *   <li><b>외부 주소 freeSolo</b> — 자동완성에 없어도 직접 입력 허용.</li>
 *   <li><b>구분자 커밋</b> — 콤마(,), 세미콜론(;), Enter, blur 시 자동 chip 확정.</li>
 *   <li><b>이메일 검증</b> — 유효하지 않으면 toast 경고 후 커밋 차단.</li>
 *   <li><b>옵션 행</b> — 1행: 이름(13/600) + 부서·직책(11/회색) / 2행: 이메일(11/회색)</li>
 *   <li><b>칩</b> — #eff6ff / #1e40af 톤. 라벨은 "이름 &lt;이메일&gt;" (외부주소면 raw email)</li>
 * </ul>
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code label} — 좌측 70px 헤더(받는이/참조/숨은참조 등)</li>
 *   <li>{@code value, onChange} — 선택된 항목 배열</li>
 *   <li>{@code autoFocus} — 첫 번째 입력칸에서 사용</li>
 *   <li>{@code trailing} — 입력칸 오른쪽 끝(지우기 X 옆)에 같은 줄로 붙일 도구.
 *       행 안에 두는 이유는 테두리·세로 정렬이 한 행으로 유지되기 때문이다.
 *       칩이 여러 줄로 늘어나도 첫 행에 고정된다(행 자체가 flex-start 정렬).</li>
 * </ul>
 */

const EMAIL_RE = /^[^\s<>@,;]+@[^\s<>@,;.]+(\.[^\s<>@,;.]+)+$/;
const isValidEmail = (s) => typeof s === "string" && EMAIL_RE.test(s.trim());

export default function RecipientAutocomplete({ label, value, onChange, autoFocus, trailing }) {
    const [inputValue, setInputValue] = useState("");

    /* 콤마/세미콜론 커밋 — MUI 가 처리하지 않는 키. ENTER/Tab/blur 는 freeSolo 가 자동 처리. */
    const handleKeyDown = (e) => {
        /* 한글 IME 조합 중의 Enter 는 «조합 확정»용이지 «후보 선택»용이 아니다.
           2벌식은 마지막 음절이 계속 조합 상태로 남아("홍길동" 의 '동') autoHighlight 와 만나면
           이름을 다 치자마자 누른 첫 Enter 가 엉뚱한 후보를 확정해 버린다.
           MUI 의 IME 가드는 keyCode 229 만 보는데 크롬 한글 Enter 는 여기 안 걸리므로 직접 막는다.
           defaultMuiPrevented 는 useAutocomplete 가 «이 키는 내가 처리하지 말라» 로 읽는 공식 신호.
           조합 중이라도 방향키는 후보 이동에 써야 하니 Enter 만 삼킨다. */
        if (e.key === "Enter" && e.nativeEvent?.isComposing) {
            e.defaultMuiPrevented = true;
            return;
        }
        if (e.key !== "," && e.key !== ";") return;
        const text = (inputValue || "").trim().replace(/[,;]\s*$/, "");
        if (!text) return;
        e.preventDefault();
        if (!isValidEmail(text)) {
            notify(`올바른 이메일 형식이 아닙니다: ${text}`, "warning");
            return;
        }
        const already = value.some(
            v => (typeof v === "string" ? v : v.email).toLowerCase() === text.toLowerCase()
        );
        if (!already) onChange([...value, text]);
        setInputValue("");
    };

    /* 다중 붙여넣기 — 콤마/세미콜론/개행/탭으로 구분된 이메일 목록을 한 번에 chip 으로 확정.
       구분자가 없는 단일 "이름 <메일>" 은 기본 동작(freeSolo)에 맡긴다(공백으로 잘리지 않도록). */
    const handlePaste = (e) => {
        const text = e.clipboardData?.getData("text") || "";
        if (!/[,;\n\r\t]/.test(text)) return;
        const emails = [];
        for (const raw of text.split(/[,;\n\r\t]+/)) {
            const tok = raw.trim();
            if (!tok) continue;
            const m = tok.match(/<([^>]+)>/);
            const email = (m ? m[1] : tok).trim();
            if (isValidEmail(email)) emails.push(email);
        }
        if (emails.length === 0) return;
        e.preventDefault();
        const seen = new Set(value.map(v => (typeof v === "string" ? v : v.email).toLowerCase()));
        const add = [];
        for (const em of emails) {
            const k = em.toLowerCase();
            if (!seen.has(k)) { seen.add(k); add.push(em); }
        }
        if (add.length) onChange([...value, ...add]);
        setInputValue("");
    };

    return (
        <Box onPasteCapture={handlePaste}
             sx={{ display: "flex", alignItems: "flex-start", borderBottom: "1px solid #eef2f6", py: 0.5 }}>
            {/* 라벨을 입력 첫 행 높이(약 27px) 안에서 세로 중앙정렬 —
                칩이 없을 땐 입력 텍스트와 정확히 중앙 정렬되고, 칩이 여러 줄로 늘어나도
                라벨이 첫 행에 고정돼 어긋나지 않는다. (alignItems:center 로 두면 다중 행에서 라벨이 블록 중앙으로 내려감) */}
            <Typography sx={{
                fontSize: 12, fontWeight: 600, color: "#64748b",
                width: 70, flexShrink: 0,
                minHeight: 27, display: "flex", alignItems: "center",
            }}>
                {label}
            </Typography>
            <AppAutocomplete
                /* autoHighlight — 첫 후보를 미리 하이라이트해 두어 «이름 치고 Enter» 만으로 확정된다.
                   (마우스로 눌러야 했던 게 원래 불편의 정체)
                   후보가 하나도 없을 땐 하이라이트할 대상이 없어 freeSolo 가 그대로 받아 가므로,
                   외부 메일 주소를 직접 치고 Enter 하는 기존 동작은 그대로 살아 있다.
                   (형식 검증은 아래 validate 가 계속 담당) */
                multiple freeSolo autoSelect autoHighlight clearOnBlur={false}
                /* 받는이는 «치고 바로 Enter» 가 기본 동작이라 후보가 빨리 떠야 한다.
                   공통 기본값(500ms)은 이름을 다 친 뒤에도 체감 공백이 생긴다. 직원·연락처 검색은
                   질의가 가볍고(그룹별 최대 10건) 자기 회사 인원 규모라 250ms 로 낮춰도 부담이 없다. */
                debounceMs={250}
                autoFocus={autoFocus}
                placeholder="이름·이메일 검색 또는 외부 주소 직접 입력"
                variant="standard"
                disableUnderline
                value={value}
                onChange={(newVal) => onChange(newVal)}
                inputValue={inputValue}
                onInputChange={setInputValue}
                onKeyDown={handleKeyDown}
                fetcher={async (q) => {
                    const res = await (mailApi.suggestRecipients
                        ? mailApi.suggestRecipients(q, 8)
                        : mailApi.suggestEmployees(q, 8));
                    const data = res?.data ?? res ?? {};
                    if (Array.isArray(data)) {
                        return data.map(e => ({ ...e, _group: "직원" }));
                    }
                    const employees = (data.employees || []).map(e => ({ ...e, _group: "직원" }));
                    const contacts  = (data.contacts  || []).map(c => ({ ...c, _group: "내 연락처" }));
                    const seen = new Set(employees.map(e => (e.email || "").toLowerCase()));
                    return [...employees, ...contacts.filter(c => !seen.has((c.email || "").toLowerCase()))];
                }}
                groupBy={(o) => (typeof o === "string" ? "" : (o._group || ""))}
                getOptionLabel={(o) => typeof o === "string" ? o : (o?.email || "")}
                isOptionEqualToValue={(a, b) =>
                    (typeof a === "string" ? a : a?.email) === (typeof b === "string" ? b : b?.email)
                }
                validate={(t) => isValidEmail(t.replace(/[,;]\s*$/, "")) ? null : `올바른 이메일 형식이 아닙니다: ${t}`}
                onCommitInvalid={(_, err) => notify(err, "warning")}
                renderTag={(v, i, getTagProps) => {
                    const lbl = typeof v === "string" ? v : `${v.name} <${v.email}>`;
                    return (
                        <Chip
                            key={i}
                            label={lbl}
                            size="small"
                            {...getTagProps({ index: i })}
                            sx={{
                                fontSize: 11, height: 22, fontWeight: 500,
                                bgcolor: "#eff6ff", color: "#1e40af",
                                "& .MuiChip-deleteIcon": { color: "#1e40af" },
                            }}
                        />
                    );
                }}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    const k = (option._group || "") + (option.id || option.email);
                    return (
                        <Box component="li" key={k} {...rest}
                            sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", py: 0.5, px: 1.2 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                                {option.name}
                                <Box component="span" sx={{ ml: 1, fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                                    {option.department || option.company || ""} {option.position || ""}
                                </Box>
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>{option.email}</Typography>
                        </Box>
                    );
                }}
                sx={{ flex: 1, minWidth: 0 }}
            />
            {/* 라벨과 같은 방식으로 첫 행 높이 안에서 세로 중앙정렬 — 칩이 늘어나도 같은 줄에 남는다. */}
            {trailing && (
                <Box sx={{
                    ml: 1, flexShrink: 0,
                    minHeight: 27, display: "flex", alignItems: "center",
                }}>
                    {trailing}
                </Box>
            )}
        </Box>
    );
}
